// Settings › Data › Contacts you already know (Find walk-through K-03, 19 Oct 2026, items 32 and 33). One line: whether Ogma knows
// who the business already talks to, and what the past-contact check did. The detail (the contact import, the
// people and firms) lives in the CRM. Also the "Contact anyway?" card for the approvals queue (task kind
// in_touch_check). L-01 (round 2, item 64) reworded the card: Ogma knows everyone in the CRM and never cold-contacts
// them; contacts kept anywhere else are added through the CRM; it ends with a button to Your contacts (/settings/data).
// Owned by L-01 in round 2.
import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useCrm } from "../../lib/session";
import { useAsync } from "../../lib/useAsync";
import { fmtNumber } from "../../lib/format";
import { Async, Notice, PageHead } from "../../components/ui";
import { Button, Card } from "../../components/kit";
import type { Crm } from "../../lib/crm";
import type { HumanTask } from "../../lib/types";
import { answerInTouch, coldContactSummary, CONTACTS_PATH, inTouchQuestion, type ColdContactSummary } from "../../lib/crmLink";

/** The old Settings › Sending › Your contacts address: the page moved to the CRM (K-03), so it opens it there. */
export function ContactsMovedPage() {
  return <Navigate to={CONTACTS_PATH} replace />;
}

/** L-01 (round 2, item 64): contacts kept anywhere else go into the CRM so they are protected too. */
export const ELSEWHERE_LINE =
  "Keep contacts anywhere else, such as another CRM, a spreadsheet or your phone? Add them to your CRM so they're protected too.";
export const CONTACTS_BUTTON = "Go to Your contacts";

const plural = (n: number, one: string, many: string) => `${fmtNumber(n)} ${n === 1 ? one : many}`;

/** The counts after "CRM link: on.", in plain words; empty ones are left out. */
export function crmLinkCounts(s: ColdContactSummary): string[] {
  const out: string[] = [];
  const knownTotal = s.known + s.people_known;
  if (knownTotal) out.push(`${plural(knownTotal, "contact or firm", "contacts and firms")} you know`);
  if (s.in_touch_firms) out.push(`${plural(s.in_touch_firms, "firm", "firms")} in a conversation now`);
  if (s.kept_out_30d) out.push(`${plural(s.kept_out_30d, "person", "people")} kept out of cold emails this month`);
  return out;
}

export function crmLinkSubtitle(s: ColdContactSummary): string {
  if (!s.on) return "Off: no contacts connected";
  const parts = ["On"];
  if (s.waiting) parts.push(`${plural(s.waiting, "person", "people")} waiting for your answer`);
  else parts.push("checking every firm");
  return parts.join(" · ");
}

function CrmLinkLine({ s }: { s: ColdContactSummary }) {
  const counts = crmLinkCounts(s);
  return (
    <Card id="crm-link" level={2} title="CRM link" subtitle={crmLinkSubtitle(s)} defaultOpen waiting={s.waiting > 0}>
      <p>
        <strong>Ogma knows everyone already in your CRM, and never sends them cold outreach.</strong>
        {s.on && counts.length ? ` ${counts.join(", ")}.` : ""}
      </p>
      {!s.on && <p className="muted small">Your CRM has no contacts yet, so for now Ogma only skips people who have written to you.</p>}
      {s.waiting > 0 && (
        <p>
          {plural(s.waiting, "person works at a firm", "people work at firms")} you are already in touch with.{" "}
          <Link to="/campaigns/to-approve">Decide whether to contact {s.waiting === 1 ? "them" : "each one"}</Link>.
        </p>
      )}
      <p>{ELSEWHERE_LINE}</p>
      <div className="fold-actions">
        <Button variant="primary" to={CONTACTS_PATH}>
          {CONTACTS_BUTTON}
        </Button>
      </div>
    </Card>
  );
}

export function CrmLinkPage() {
  const crm = useCrm();
  const data = useAsync(() => coldContactSummary(crm), [crm]);
  return (
    <div className="page find-a">
      <PageHead code="0.28" title="CRM link" lede="Nobody you already know or talk to gets a cold email from Ogma." />
      <Async state={data} label="Checking your CRM link">
        {(s) => <CrmLinkLine s={s} />}
      </Async>
    </div>
  );
}

/** The approvals card for "Already in touch with Jo Smith here. Contact Sam anyway?" (kind in_touch_check, 4.10). */
export function InTouchTask({ crm, task, onDone }: { crm: Crm; task: HumanTask; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const p = task.payload ?? {};
  const first = String(p.person_name ?? "").split(" ")[0] || "them";
  const answer = async (contact: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await answerInTouch(crm, task.id, contact);
      onDone();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };
  return (
    <div>
      <p className="card-lede">
        <strong>{inTouchQuestion(task)}</strong>
      </p>
      <p className="muted small">
        Nothing goes to {first} until you decide. Yes: {first} gets this campaign like any new prospect. No: {first} is left out and not asked
        about again.
      </p>
      <p className="small">
        {typeof p.with_person_id === "string" && <Link to={`/contacts/people/${p.with_person_id}`}>See the conversation with {String(p.with_name ?? "them")}</Link>}
        {typeof p.with_person_id === "string" && task.entity_id && " · "}
        {task.entity_id && <Link to={`/contacts/people/${task.entity_id}`}>Open {first}</Link>}
      </p>
      <Notice text={error} tone="bad" />
      <div className="action-bar">
        <button type="button" className="btn" disabled={busy} onClick={() => void answer(false)}>
          No, leave {first} out
        </button>
        <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void answer(true)}>
          {busy ? "Saving…" : `Yes, contact ${first}`}
        </button>
      </div>
    </div>
  );
}
